import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { Helper } from "src/app/core/helpers/helper";
import { HistoryService } from "../../services/history.service";
import { MapHelperService } from "../../services/mapInit.service";
import { VehicleTrackingService } from "../../services/vehicle-tracking.service";

declare const google: any;

interface TrackPoint {
  lat: number;
  lng: number;
  time: number;
  /** Recorded GPS speed in metres per second, when the history row has one. */
  speed: number | null;
}

interface TrackEvent {
  id: string;
  label: string;
  time: number;
}

interface TrackBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

@Component({
  selector: "app-trip-vehicle-track",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./trip-vehicle-track.component.html",
  styleUrl: "./trip-vehicle-track.component.scss"
})
export class TripVehicleTrackComponent implements OnInit, OnDestroy {
  @Input() booking: any = null;
  @Input() bookingId = "";
  /** Shrink the map so it can sit inside the caller pop-up. */
  @Input() compact = false;
  /** Park the cursor on the latest GPS point — the live driver position. */
  @Input() live = false;

  readonly mapElementId = "tripVehicleTrackMap-" + Math.random().toString(36).slice(2, 9);

  readonly speeds = [1, 2, 5, 10];

  loading = true;
  note = "";
  hover = "";
  playing = false;
  speed = 1;
  index = 0;
  playhead = 0;
  spanStart = 0;
  spanEnd = 0;
  speedNow: number | null = null;
  mapMode: "google" | "mapbox" | "plot" | "" = "";
  points: TrackPoint[] = [];
  events: TrackEvent[] = [];
  car: TrackPoint | null = null;
  pickup: TrackPoint | null = null;
  drop: TrackPoint | null = null;
  bounds: TrackBounds | null = null;

  private timer: ReturnType<typeof setInterval> | null = null;
  private lastTick = 0;
  private tripTimings: any = null;
  private map: any = null;
  /** Loaded with import("mapbox-gl") only when this track opens a Mapbox map. */
  private mb: any = null;
  private line: any = null;
  private cursor: any = null;
  private info: any = null;
  private popup: any = null;
  private markers: any[] = [];

  private history = inject(HistoryService);
  private tracking = inject(VehicleTrackingService);
  private mapHelper = inject(MapHelperService);
  private cdr = inject(ChangeDetectorRef);
  public helper = inject(Helper);

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.stop();
    this.clearMap();
  }

  toggle(): void {
    if (this.playing) this.stop();
    else this.play();
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  clock(): string {
    if (!this.playhead) return this.points.length ? "Start" : "";
    return this.formatClock(this.playhead);
  }

  speedText(): string {
    if (this.speedNow == null) return "Speed —";
    return `Speed ${Math.round(this.speedNow * 3.6)} km/h`;
  }

  onScrub(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(value)) return;
    this.playhead = value;
    this.syncCar();
  }

  jumpTo(event: TrackEvent): void {
    this.playhead = Math.min(this.spanEnd, Math.max(this.spanStart, event.time));
    this.syncCar();
  }

  eventPercent(event: TrackEvent): number {
    const span = this.spanEnd - this.spanStart;
    if (span <= 0) return 0;
    const time = Math.min(this.spanEnd, Math.max(this.spanStart, event.time));
    return ((time - this.spanStart) / span) * 100;
  }

  trailSvg(): string {
    return this.points.map((point) => this.toSvg(point).join(",")).join(" ");
  }

  playedSvg(): string {
    if (!this.car) return "";
    const passed = this.points.filter((point) => point.time <= this.playhead);
    const nodes = passed.length ? passed : this.points.slice(0, 1);
    const last = nodes[nodes.length - 1];
    const withCar = last && (last.lat !== this.car.lat || last.lng !== this.car.lng) ? [...nodes, this.car] : nodes;
    return withCar.map((point) => this.toSvg(point).join(",")).join(" ");
  }

  markerSvg(point: TrackPoint | null): { x: number; y: number } | null {
    if (!point || !this.bounds) return null;
    const [x, y] = this.toSvg(point);
    return { x, y };
  }

  onPlotMove(event: MouseEvent): void {
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height || !this.points.length) return;
    const x = ((event.clientX - rect.left) / rect.width) * 1000;
    const y = ((event.clientY - rect.top) / rect.height) * 600;
    let best = -1;
    let bestD = 80 * 80;
    this.points.forEach((point, i) => {
      const [px, py] = this.toSvg(point);
      const d = (px - x) ** 2 + (py - y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    this.hover = best >= 0 ? this.formatTime(this.points[best].time) : "";
  }

  private async load(): Promise<void> {
    this.loading = true;
    const uniqueId = this.booking?.uniqueId;
    let driverId = this.booking?.confirmedDriver?.id || this.booking?.confirmedDriver?._id || "";
    let from = 0;
    let to = Date.now();
    this.pickup = this.asPoint(this.booking?.pickupAddress);
    const destinations = this.booking?.actualDestinationAddresses?.length ? this.booking.actualDestinationAddresses : this.booking?.destinationAddresses;
    this.drop = this.asPoint(destinations?.[destinations.length - 1]);

    try {
      if (uniqueId != null && uniqueId !== "") {
        const res: any = await firstValueFrom(this.tracking.getTripByNumber(uniqueId));
        const data = res?.data?.data || res?.data || {};
        driverId = data.driver?._id || driverId;
        this.tripTimings = data.trip?.timings || null;
        const accepted = Number(data.trip?.timings?.acceptedAt || data.trip?.timings?.assignedAt || 0);
        const completed = Number(data.trip?.timings?.completedAt || 0);
        from = accepted || Number(data.playbackWindow?.from || 0);
        to = completed || Number(data.playbackWindow?.to || Date.now());
        this.pickup = this.asPoint(data.trip?.pickup) || this.pickup;
        const dests = data.trip?.destinations || [];
        this.drop = this.asPoint(dests[dests.length - 1]) || this.drop;
      }
    } catch {
      driverId = driverId || "";
    }

    let points: TrackPoint[] = [];
    let source = "";
    if (driverId) {
      try {
        const res: any = await firstValueFrom(this.tracking.getDriverHistory(String(driverId), { from, to }));
        const data = res?.data?.data || res?.data || {};
        points = (data.points || []).map((point: any) => this.gpsPoint(point)).filter((point: TrackPoint | null): point is TrackPoint => !!point);
        if (from && to) points = points.filter((point) => !point.time || (point.time >= from && point.time <= to));
        if (points.length) source = "history";
      } catch {
        points = [];
      }
    }

    if (!points.length && this.bookingId) {
      try {
        const body: any = await firstValueFrom(this.history.getBookingLocation(this.bookingId, { isShowErrorToast: false }));
        const locations = body?.data?.locations || [];
        points = locations
          .map((location: any) => this.asPoint(location))
          .filter((point: TrackPoint | null): point is TrackPoint => !!point);
        if (points.length) source = "location";
      } catch {
        points = [];
      }
    }

    this.points = points.filter((point) => point.time > 0).sort((a, b) => a.time - b.time);
    this.events = this.collectEvents(this.tripTimings, this.booking?.statusTimeline);
    const eventTimes = this.events.map((event) => event.time);
    const first = this.points[0]?.time || 0;
    const last = this.points[this.points.length - 1]?.time || first;
    const marks = [first, last, ...eventTimes].filter((time) => time > 0);
    this.spanStart = marks.length ? Math.min(...marks) : 0;
    this.spanEnd = marks.length ? Math.max(...marks) : this.spanStart;
    this.playhead = this.live && this.spanEnd ? this.spanEnd : this.spanStart;
    this.syncCar();
    this.loading = false;
    if (this.live && source === "history") this.note = "Live driver is the latest GPS point.";
    else if (source === "history") this.note = "Replay follows the recorded GPS times.";
    else if (source === "location") this.note = "Driver history had no GPS points. Showing the locations recorded for this trip.";
    else if (!driverId) this.note = "No driver was assigned, so there is no GPS trail.";
    else this.note = "No GPS points were stored for this trip.";
    this.prepareBounds();
    this.chooseMap();
    this.cdr.detectChanges();
  }

  private play(): void {
    if (this.points.length < 2 || this.spanEnd <= this.spanStart) return;
    if (this.playhead >= this.spanEnd) this.playhead = this.spanStart;
    this.playing = true;
    this.lastTick = performance.now();
    this.syncCar();
    this.timer = setInterval(() => {
      const now = performance.now();
      const delta = now - this.lastTick;
      this.lastTick = now;
      this.playhead = Math.min(this.spanEnd, this.playhead + delta * this.speed);
      this.syncCar();
      if (this.playhead >= this.spanEnd) this.stop();
      this.cdr.detectChanges();
    }, 50);
  }

  stop(): void {
    this.playing = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private chooseMap(): void {
    const googleReady = this.helper.mapTypeGoogle() && typeof google !== "undefined" && !!google?.maps;
    const mapboxReady = this.helper.mapTypeMapBox() && !!this.helper.MapBoxAccessToken;
    if (googleReady) this.mapMode = "google";
    else if (mapboxReady) this.mapMode = "mapbox";
    else this.mapMode = "plot";
    this.cdr.detectChanges();
    if (this.mapMode === "google") setTimeout(() => this.drawGoogle(), 180);
    if (this.mapMode === "mapbox") setTimeout(() => this.drawMapbox(), 180);
  }

  private drawGoogle(): void {
    const center = this.points[0] || this.pickup || { lat: 0, lng: 0 };
    try {
      this.map = this.mapHelper.initializeGoogleMap(this.mapElementId, {
        zoom: 13,
        center: { lat: center.lat, lng: center.lng },
        mapTypeControl: false,
        streetViewControl: false
      });
    } catch {
      this.mapMode = "plot";
      this.cdr.detectChanges();
      return;
    }
    if (!this.map) {
      this.mapMode = "plot";
      this.cdr.detectChanges();
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    const path = this.points.map((point) => ({ lat: point.lat, lng: point.lng }));
    this.line = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#0f766e",
      strokeOpacity: 1,
      strokeWeight: 4,
      map: this.map
    });
    this.info = new google.maps.InfoWindow();
    this.line.addListener("mousemove", (event: any) => {
      const nearest = this.nearest(event.latLng.lat(), event.latLng.lng());
      if (!nearest) return;
      const node = document.createElement("div");
      node.textContent = this.formatTime(nearest.time);
      this.info.setContent(node);
      this.info.setPosition(event.latLng);
      this.info.open(this.map);
    });
    this.line.addListener("mouseout", () => this.info.close());
    path.forEach((point) => bounds.extend(point));
    this.pinGoogle(this.pickup, "A", "#0f766e", "Pickup");
    this.pinGoogle(this.drop, "B", "#991b1b", "Drop-off");
    if (this.pickup) bounds.extend({ lat: this.pickup.lat, lng: this.pickup.lng });
    if (this.drop) bounds.extend({ lat: this.drop.lat, lng: this.drop.lng });
    const frame = (): void => {
      const element = document.getElementById(this.mapElementId);
      if (!this.map || !element) return;
      google.maps.event.trigger(this.map, "resize");
      if (!bounds.isEmpty()) this.map.fitBounds(bounds);
    };
    frame();
    setTimeout(frame, 300);
    this.moveCursor();
    setTimeout(() => this.fallbackIfMapTilesFail(), 700);
  }

  /** A city without a map key still gets the GPS trail, markers, hover, and replay. */
  private fallbackIfMapTilesFail(): void {
    const map = document.getElementById(this.mapElementId);
    if (!map || this.mapMode !== "google") return;
    const failed = /can't load Google Maps/i.test(map.innerText || "") || !!map.querySelector(".gm-err-container, .gm-err-message");
    if (!failed) return;
    this.clearMap();
    this.mapMode = "plot";
    this.cdr.detectChanges();
  }

  private pinGoogle(point: TrackPoint | null, label: string, color: string, title: string): void {
    if (!point || !this.map) return;
    this.markers.push(
      new google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        map: this.map,
        title,
        label: { text: label, color: "#ffffff", fontWeight: "700" },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2
        }
      })
    );
  }

  private async drawMapbox(): Promise<void> {
    this.mb = await this.mapHelper.loadMapbox();
    const center = this.points[0] || this.pickup;
    const element = document.getElementById(this.mapElementId);
    if (!element || !center) {
      this.mapMode = "plot";
      this.cdr.detectChanges();
      return;
    }
    try {
      this.mb.accessToken = this.helper.MapBoxAccessToken;
      this.map = new this.mb.Map({
        container: element,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [center.lng, center.lat],
        zoom: 12
      });
    } catch {
      this.mapMode = "plot";
      this.cdr.detectChanges();
      return;
    }
    this.popup = new this.mb.Popup({ closeButton: false, closeOnClick: false });
    this.map.on("load", () => {
      const line = {
        type: "Feature",
        geometry: { type: "LineString", coordinates: this.points.map((point) => [point.lng, point.lat]) }
      };
      this.map.addSource("trip-track", { type: "geojson", data: line });
      this.map.addLayer({
        id: "trip-track-line",
        type: "line",
        source: "trip-track",
        paint: { "line-color": "#0f766e", "line-width": 4 }
      });
      this.map.on("mousemove", "trip-track-line", (event: any) => {
        const lng = event.lngLat?.lng;
        const lat = event.lngLat?.lat;
        const nearest = this.nearest(lat, lng);
        if (!nearest) return;
        this.popup.setLngLat(event.lngLat).setText(this.formatTime(nearest.time)).addTo(this.map);
      });
      this.map.on("mouseleave", "trip-track-line", () => this.popup.remove());
      this.pinMapbox(this.pickup, "A", "#0f766e");
      this.pinMapbox(this.drop, "B", "#991b1b");
      const corners = [...this.points, this.pickup, this.drop].filter(Boolean) as TrackPoint[];
      if (corners.length) {
        const fit = new this.mb.LngLatBounds([corners[0].lng, corners[0].lat], [corners[0].lng, corners[0].lat]);
        corners.forEach((point) => fit.extend([point.lng, point.lat]));
        this.map.fitBounds(fit, { padding: 48 });
      }
      this.moveCursor();
    });
  }

  private pinMapbox(point: TrackPoint | null, label: string, color: string): void {
    if (!point || !this.map) return;
    const el = document.createElement("div");
    el.className = "trip-track-pin";
    el.style.background = color;
    el.textContent = label;
    this.markers.push(new this.mb.Marker({ element: el }).setLngLat([point.lng, point.lat]).addTo(this.map));
  }

  private syncCar(): void {
    const sample = this.sample(this.playhead);
    if (!sample) return;
    this.car = { lat: sample.lat, lng: sample.lng, time: this.playhead, speed: sample.speed };
    this.index = sample.index;
    this.speedNow = sample.speed;
    this.moveCursor();
  }

  /**
   * Position at `time` using only the recorded points.
   * Between two points the marker is a straight smoothing. Before the first
   * point and after the last point it stays on that real point.
   * Speed is the recorded value on the point at or just before `time`.
   */
  private sample(time: number): { lat: number; lng: number; speed: number | null; index: number } | null {
    const points = this.points;
    if (!points.length) return null;
    if (time <= points[0].time) return { lat: points[0].lat, lng: points[0].lng, speed: points[0].speed, index: 0 };
    const last = points.length - 1;
    if (time >= points[last].time) return { lat: points[last].lat, lng: points[last].lng, speed: points[last].speed, index: last };
    let lo = 0;
    let hi = last;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (points[mid].time <= time) lo = mid;
      else hi = mid;
    }
    const from = points[lo];
    const to = points[hi];
    const span = to.time - from.time;
    const portion = span > 0 ? (time - from.time) / span : 0;
    return {
      lat: from.lat + (to.lat - from.lat) * portion,
      lng: from.lng + (to.lng - from.lng) * portion,
      speed: from.speed,
      index: lo
    };
  }

  private collectEvents(timings: any, timeline: any[]): TrackEvent[] {
    const rows = Array.isArray(timeline) ? timeline : [];
    const firstStatus = (status: number): number => {
      const hit = rows.find((row) => Number(row?.status) === status && Number(row?.time) > 0);
      return hit ? Number(hit.time) : 0;
    };
    const found: TrackEvent[] = [];
    const add = (id: string, label: string, time: number): void => {
      if (!Number.isFinite(time) || time <= 0) return;
      if (found.some((event) => event.id === id)) return;
      found.push({ id, label, time });
    };
    add("accepted", "Accepted", Number(timings?.acceptedAt) || firstStatus(20));
    add("pickup", "Arrived at pickup", Number(timings?.arrivedAtPickupAt) || firstStatus(40));
    add("picked", "Picked up", Number(timings?.pickedAt) || firstStatus(41));
    add("drop", "Dropped off", Number(timings?.droppedAt) || firstStatus(42) || Number(timings?.completedAt) || firstStatus(70));
    return found.sort((a, b) => a.time - b.time);
  }

  private moveCursor(): void {
    const point = this.car;
    if (!point) return;
    if (this.mapMode === "google" && this.map && google?.maps) {
      const position = { lat: point.lat, lng: point.lng };
      if (!this.cursor) {
        this.cursor = new google.maps.Marker({
          position,
          map: this.map,
          zIndex: 5,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: "#115e59",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2
          }
        });
      } else {
        this.cursor.setPosition(position);
      }
    }
    if (this.mapMode === "mapbox" && this.map) {
      if (!this.cursor) {
        const el = document.createElement("div");
        el.className = "trip-track-cursor";
        this.cursor = new this.mb.Marker({ element: el }).setLngLat([point.lng, point.lat]).addTo(this.map);
      } else {
        this.cursor.setLngLat([point.lng, point.lat]);
      }
    }
  }

  private clearMap(): void {
    this.markers.forEach((marker) => marker.setMap?.(null) || marker.remove?.());
    this.markers = [];
    this.line?.setMap?.(null);
    this.cursor?.setMap?.(null);
    this.cursor?.remove?.();
    this.info?.close?.();
    this.popup?.remove?.();
    if (this.mapMode === "mapbox") this.map?.remove?.();
    this.map = null;
    this.line = null;
    this.cursor = null;
  }

  private gpsPoint(point: any): TrackPoint | null {
    if (!point || point.type) return null;
    return this.asPoint(point);
  }

  private asPoint(value: any): TrackPoint | null {
    if (!value) return null;
    const lat = Number(value.latitude ?? value.lat);
    const lng = Number(value.longitude ?? value.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const speed = Number(value.speed);
    return { lat, lng, time: Number(value.time || 0), speed: Number.isFinite(speed) ? speed : null };
  }

  private nearest(lat: number, lng: number): TrackPoint | null {
    let best: TrackPoint | null = null;
    let bestD = Infinity;
    this.points.forEach((point) => {
      const d = (point.lat - lat) ** 2 + (point.lng - lng) ** 2;
      if (d < bestD) {
        bestD = d;
        best = point;
      }
    });
    return best;
  }

  private prepareBounds(): void {
    const all = [...this.points, this.pickup, this.drop].filter(Boolean) as TrackPoint[];
    if (!all.length) {
      this.bounds = null;
      return;
    }
    const lats = all.map((point) => point.lat);
    const lngs = all.map((point) => point.lng);
    const latSpan = Math.max(...lats) - Math.min(...lats) || 0.01;
    const lngSpan = Math.max(...lngs) - Math.min(...lngs) || 0.01;
    this.bounds = {
      minLat: Math.min(...lats) - latSpan * 0.12,
      maxLat: Math.max(...lats) + latSpan * 0.12,
      minLng: Math.min(...lngs) - lngSpan * 0.12,
      maxLng: Math.max(...lngs) + lngSpan * 0.12
    };
  }

  private toSvg(point: TrackPoint): [number, number] {
    const bounds = this.bounds;
    if (!bounds) return [0, 0];
    const x = ((point.lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * 1000;
    const y = (1 - (point.lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * 600;
    return [x, y];
  }

  formatClock(time: number): string {
    if (!time) return "Time not stored";
    const zone = this.booking?.timeZone || this.booking?.timezone || this.helper.displayTimezone;
    try {
      return this.helper.moment(time).tz(zone).format("h:mm:ss A");
    } catch {
      return new Date(time).toLocaleTimeString();
    }
  }

  private formatTime(time: number): string {
    return this.formatClock(time);
  }
}
